import Commons from '../../../../Services/Common/Common';

/**
 * LEG_BASIC (법규준수평가) 스키마
 * - fields: 그리드/폼 필드 메타(필수값, 라벨, 초기값)
 * - mapper: 조회/저장/삭제 API payload 매핑 규칙
 *
 * NOTE:
 * - 화면/그리드는 camelCase 필드를 사용한다.
 * - API payload 키는 백엔드 DTO 규칙(PascalCase)을 따르도록 매핑한다.
 * - 상세 데이터 테이블: TB_BO_COMPLIANCE_EVAL
 * - API 요청 JSON 키는 백엔드 DTO와 동일해야 함(구 LegBasic API는 LwYear/LwBranch/LwEva* 등 유지).
 */
export const LEG_BASIC_Schema = {
  fields: {
    // detail rows (모달 그리드)
    evalId: { initial: '', label: '평가일련번호' },
    lwNm: { initial: '', label: '법령명', required: 'Y' },
    checkItem: { initial: '', label: '점검 항목', required: 'Y' },
    checkContent: { initial: '', label: '점검 내용', required: 'Y' },
    complianceStatus: { initial: '', label: '이행 여부' },
    remark: { initial: '', label: '비고' },

    // master context (조회/저장에 필요)
    evalYear: { initial: '', label: '년도' },
    evalHalf: { initial: '상반기', label: '상/하반기' },
    lawNm: { initial: '산업안전보건법', label: '법령명' },
    startDt: { initial: '', label: '평가기간(시작)' },
    endDt: { initial: '', label: '평가기간(종료)' },
  },

  mapper: {
    // 마스터 조회(왼쪽 그리드)
    search1: {
      LwYear: (_, context) => Commons.EncryptAES(context.search?.year),
      CustCd: (_, context) => Commons.EncryptAES(context.search?.custCd),
      SearchText: (_, context) =>
        Commons.EncryptAES(context.search?.searchText),
      UserId: (_, context) => context.userInfo?.userid,
    },

    // 상세 조회(모달)
    search2: {
      LwYear: (_, context) => Commons.EncryptAES(context.search?.year),
      CustCd: (_, context) => Commons.EncryptAES(context.search?.custCd),
      LwBranch: (_, context) => Commons.EncryptAES(context.evalHalf),
      LwLawNm: (_, context) =>
        Commons.EncryptAES(context.lawNm || '산업안전보건법'),
      UserId: (_, context) => context.userInfo?.userid,
    },

    // 상세 저장 → SaveLegBasicU1 (서버 DTO 프로퍼티명 유지, SQL에서 CHECK_* 컬럼에 바인딩)
    save1: {
      LwCd: (row) =>
        Commons.EncryptAES(
          row.evalId != null && row.evalId !== ''
            ? String(row.evalId)
            : (row.lwCd ?? '')
        ),
      LwNm: (row) => Commons.EncryptAES(row.lwNm),
      LwProvisions: (row) => Commons.EncryptAES(row.checkItem ?? ''),
      LwMain: (row) => Commons.EncryptAES(row.checkContent ?? ''),
      LwCompliance: (row) => Commons.EncryptAES(row.complianceStatus ?? ''),
      LwRemark: (row) => Commons.EncryptAES(row.remark ?? ''),

      LwYear: (_, context) => Commons.EncryptAES(context.search?.year),
      LwBranch: (row) => Commons.EncryptAES(row.evalHalf),
      LwLawNm: (row) =>
        Commons.EncryptAES(row.lawNm || '산업안전보건법'),
      CustCd: (_, context) => Commons.EncryptAES(context.search?.custCd),
      UserId: (_, context) => context.userInfo?.userid,
    },

    // 마스터(평가기간) 저장 - 전체 적용 (배열 처리)
    save2: {
      CustCd: (_, context) => Commons.EncryptAES(context.search?.custCd),
      LwYear: (_, context) => Commons.EncryptAES(context.search?.year),
      LwBranch: (row) => Commons.EncryptAES(row.evalHalf),
      LwEvaStartDt: (row) => Commons.EncryptAES(row.startDt),
      LwEvaEndDt: (row) => Commons.EncryptAES(row.endDt),
      LwLawNm: (row) =>
        Commons.EncryptAES(row.lawNm || '산업안전보건법'),
      UserId: (_, context) => context.userInfo?.userid,
    },

    // 마스터 삭제(왼쪽 그리드 선택행 삭제)
    delete1: {
      CustCd: (_, context) => Commons.EncryptAES(context.search?.custCd),
      LwYear: (row) => Commons.EncryptAES(row.evalYear),
      LwBranch: (row) => Commons.EncryptAES(row.evalHalf),
      LwLawNm: (row) =>
        Commons.EncryptAES(row.lawNm || '산업안전보건법'),
      UserId: (_, context) => context.userInfo?.userid,
    },

    // 점검표 선택용 법령 목록
    search3: {
      CustCd: (_, context) => Commons.EncryptAES(context.search?.custCd),
      UserId: (_, context) => context.userInfo?.userid,
    },

    // 신규 등록 법령(양식) 삭제 — 저장된 기본정보 평가는 유지
    deleteLaw: {
      CustCd: (_, context) => Commons.EncryptAES(context.search?.custCd),
      LwLawNm: (row) => Commons.EncryptAES(row.lawNm || ''),
      UserId: (_, context) => context.userInfo?.userid,
    },

    // 상세 삭제(모달 선택행 삭제)
    delete2: {
      LwCd: (row) =>
        Commons.EncryptAES(
          row.evalId != null && row.evalId !== ''
            ? String(row.evalId)
            : (row.lwCd ?? '')
        ),
      UserId: (_, context) => context.userInfo?.userid,
    },
  },
};
